import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subscription, catchError, of } from 'rxjs';
import { gsap } from 'gsap';
import { ScrollTrigger, CSSPlugin } from 'gsap/all';
import { ApiClientService } from '../../services/api-client.service';
import { LocationService, UserLocation } from '../../services/location.service';
import { ClassData } from '../../shared/models/class.model';

let nearbyClassesPluginsRegistered = false;

@Component({
  selector: 'app-nearby-classes',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './nearby-classes.component.html',
  styleUrl: './nearby-classes.component.scss',
})
export class NearbyClassesComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('nearbySection', { static: true }) nearbySection!: ElementRef<HTMLElement>;

  private readonly isBrowser: boolean;
  private locationSub!: Subscription;

  classes: ClassData[] = [];
  loading = false;
  error = '';
  location: UserLocation | null = null;
  readonly placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImEiIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiMxMGI5ODEiLz48c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiMwNjk2NjAiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2EpIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5DbGFzczwvdGV4dD48L3N2Zz4=';

  get viewMoreParams(): Record<string, string> {
    return this.location
      ? { city: this.location.city, state: this.location.state }
      : {};
  }

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private readonly apiClient: ApiClientService,
    private readonly locationService: LocationService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser && !nearbyClassesPluginsRegistered) {
      gsap.registerPlugin(ScrollTrigger, CSSPlugin);
      nearbyClassesPluginsRegistered = true;
    }
  }

  ngOnInit(): void {
    this.locationSub = this.locationService.location$.subscribe((loc) => {
      this.location = loc;
      this.loadClasses(loc);
    });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || window.innerWidth <= 768) return;
    this.initAnimations();
  }

  ngOnDestroy(): void {
    this.locationSub?.unsubscribe();
  }

  loadClasses(loc: UserLocation | null): void {
    this.loading = true;
    this.error = '';

    let endpoint = '/classes/public?limit=12';
    if (loc) {
      endpoint += `&lat=${loc.lat}&lng=${loc.lng}&radius=${loc.radius}`;
    }

    this.apiClient.get<ClassData[]>(endpoint).pipe(
      catchError(() => {
        this.error = 'Failed to load nearby classes. Please try again.';
        return of([]);
      })
    ).subscribe((classes) => {
      this.classes = classes.slice(0, 12);
      this.loading = false;
    });
  }

  formatTime(time: string): string {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? 'PM' : 'AM'}`;
  }

  private initAnimations(): void {
    const el = this.nearbySection.nativeElement;
    gsap.fromTo(el, { y: 50, opacity: 0 }, {
      y: 0, opacity: 1, duration: 1, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none reverse' },
    });
    const cards = el.querySelectorAll('.event-card');
    gsap.fromTo(cards, { y: 30, opacity: 0 }, {
      y: 0, opacity: 1, duration: 0.8, stagger: 0.08, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 70%', toggleActions: 'play none none reverse' },
    });
  }
}
